export type LeadQuestionOption = {
  value: string
  label: string
  /** Score contribution when selected; falls back to index+1 for radio. */
  score?: number
}

export type LeadQuestion = {
  id: string
  question: string
  type: 'slider' | 'radio' | 'checkbox' | 'number' | 'text' | 'textarea'
  options?: LeadQuestionOption[]
  weight?: number
  required?: boolean
  min?: number
  max?: number
}

const TYPES = new Set(['slider', 'radio', 'checkbox', 'number', 'text', 'textarea'])

function sanitizeOptions(raw: unknown): LeadQuestionOption[] {
  if (!Array.isArray(raw)) return []
  const options: LeadQuestionOption[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    const value = String(rec.value ?? rec.label ?? '').trim()
    const label = String(rec.label ?? rec.value ?? '').trim()
    if (!value || !label) continue
    const scoreRaw = Number(rec.score)
    options.push({
      value: value.slice(0, 80),
      label: label.slice(0, 120),
      ...(Number.isFinite(scoreRaw) ? { score: Math.max(0, Math.min(100, Math.round(scoreRaw))) } : {}),
    })
  }
  return options.slice(0, 20)
}

export function sanitizeQuestions(raw: unknown): LeadQuestion[] {
  if (!Array.isArray(raw)) return []
  const questions: LeadQuestion[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    const type = String(rec.type || 'radio')
    if (!TYPES.has(type)) continue
    const id = String(rec.id || `q${questions.length + 1}`).slice(0, 40)
    const question = String(rec.question || '').trim().slice(0, 280)
    if (!question) continue
    questions.push({
      id,
      question,
      type: type as LeadQuestion['type'],
      options: sanitizeOptions(rec.options),
      weight: Number.isFinite(Number(rec.weight)) ? Math.max(0, Math.min(20, Number(rec.weight))) : 1,
      required: rec.required !== false,
      min: Number.isFinite(Number(rec.min)) ? Number(rec.min) : undefined,
      max: Number.isFinite(Number(rec.max)) ? Number(rec.max) : undefined,
    })
  }
  return questions.slice(0, 20)
}

export function answerValueForQuestion(question: LeadQuestion, raw: unknown): number {
  if (question.type === 'number' || question.type === 'slider') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }
  if (question.type === 'checkbox') {
    const selected = Array.isArray(raw) ? raw.map(String) : []
    return selected.reduce((sum, value) => {
      const opt = question.options?.find((item) => item.value === value)
      if (!opt) return sum
      return sum + (Number.isFinite(Number(opt.score)) ? Number(opt.score) : 1)
    }, 0)
  }
  if (question.type === 'radio') {
    const value = String(raw ?? '')
    const index = question.options?.findIndex((item) => item.value === value) ?? -1
    if (index < 0) return 0
    const opt = question.options![index]!
    if (Number.isFinite(Number(opt.score))) return Number(opt.score)
    return index + 1
  }
  return String(raw ?? '').trim() ? 1 : 0
}

export function isQuestionAnswered(question: LeadQuestion, raw: unknown): boolean {
  if (raw == null || raw === '') return false
  if (question.type === 'radio') {
    return typeof raw === 'string' && Boolean(question.options?.some((option) => option.value === raw))
  }
  if (question.type === 'checkbox') {
    return Array.isArray(raw)
      && raw.length > 0
      && raw.every((value) => question.options?.some((option) => option.value === String(value)))
  }
  if (question.type === 'number' || question.type === 'slider') {
    const value = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(value)
      && (question.min == null || value >= question.min)
      && (question.max == null || value <= question.max)
  }
  return typeof raw === 'string' && raw.trim().length > 0
}

export function validateQuizAnswers(
  questions: LeadQuestion[],
  answers: Record<string, unknown>,
): { valid: boolean; missingQuestionIds: string[]; answers: Record<string, unknown> } {
  const questionIds = new Set(questions.map((question) => question.id))
  const validatedAnswers: Record<string, unknown> = {}

  for (const id of Object.keys(answers)) {
    if (!questionIds.has(id)) {
      return { valid: false, missingQuestionIds: [], answers: {} }
    }
  }

  const missingQuestionIds: string[] = []
  for (const question of questions) {
    const raw = answers[question.id]
    if (!isQuestionAnswered(question, raw)) {
      if (question.required !== false) missingQuestionIds.push(question.id)
      continue
    }

    if (question.type === 'number' || question.type === 'slider') {
      validatedAnswers[question.id] = typeof raw === 'number' ? raw : Number(raw)
    }
    else if (question.type === 'checkbox') {
      validatedAnswers[question.id] = (raw as unknown[]).map(String)
    }
    else if (question.type === 'text' || question.type === 'textarea') {
      const value = (raw as string).trim()
      if (value.length > 2000) {
        return { valid: false, missingQuestionIds: [question.id], answers: {} }
      }
      validatedAnswers[question.id] = value
    }
    else {
      validatedAnswers[question.id] = raw
    }
  }

  return {
    valid: missingQuestionIds.length === 0,
    missingQuestionIds,
    answers: validatedAnswers,
  }
}
